import React, { useEffect, useMemo, useState, Component } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Search, ChevronDown, FileText, Tag } from 'lucide-react';
import { KnowledgeSource, KnowledgeSourceStatus } from '../types';
import { useAgent } from '../contexts/AgentContext';
import { AppCard } from '../components/base/AppCard';
import { AppButton } from '../components/base/AppButton';
import { AppBadge } from '../components/base/AppBadge';
import { Toast } from '../components/Toast';
import { SourceTypeSelectionModal, AddDocumentSourceModal, AddPromptSourceModal, AddQASourceModal, ManageKnowledgeSourceModal, ViewSourceDetailModal } from '../components/modals/KnowledgeModals';
import * as mockApi from '../services/mockApi';
type TabType = 'documents' | 'prompts' | 'qa';
export function ConoscenzaPage() {
  const {
    activeAgentId
  } = useAgent();
  const [sources, setSources] = useState<KnowledgeSource[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('Tutti');
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('documents');
  // Modal states
  const [showTypeSelectionModal, setShowTypeSelectionModal] = useState(false);
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [showAddPromptModal, setShowAddPromptModal] = useState(false);
  const [showAddQAModal, setShowAddQAModal] = useState(false);
  const [managingSource, setManagingSource] = useState<KnowledgeSource | null>(null);
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  useEffect(() => {
    loadSources();
  }, [activeAgentId]);
  const loadSources = async () => {
    setIsLoading(true);
    try {
      const data = await mockApi.getKnowledgeSources(activeAgentId);
      setSources(data);
    } catch (error) {
      setToast({
        message: 'Errore nel caricamento delle fonti',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleCreate = async (data: Omit<KnowledgeSource, 'id'>) => {
    try {
      const newSource = await mockApi.createKnowledgeSource(data, activeAgentId);
      setSources(prev => [...prev, newSource]);
      setToast({
        message: 'Fonte aggiunta',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: 'Errore nella creazione',
        type: 'error'
      });
      throw error;
    }
  };
  const handleSaveManage = async (id: string, data: Partial<KnowledgeSource>) => {
    try {
      const updated = await mockApi.updateKnowledgeSource(id, data, activeAgentId);
      setSources(prev => prev.map(s => s.id === id ? updated : s));
      setToast({
        message: 'Fonte aggiornata',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: 'Errore nel salvataggio',
        type: 'error'
      });
      throw error;
    }
  };
  const handleAddFile = async (sourceId: string, file: {
    name: string;
    size: number;
  }) => {
    try {
      await mockApi.addKnowledgeFile(sourceId, file, activeAgentId);
      await loadSources();
      setToast({
        message: 'File aggiunto',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: "Errore nell'aggiunta del file",
        type: 'error'
      });
      throw error;
    }
  };
  const handleRemoveFile = async (sourceId: string, fileId: string) => {
    try {
      await mockApi.removeKnowledgeFile(sourceId, fileId, activeAgentId);
      await loadSources();
      setToast({
        message: 'File rimosso',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: 'Errore nella rimozione del file',
        type: 'error'
      });
      throw error;
    }
  };
  const handleTypeSelect = (type: 'documents' | 'prompt' | 'qa') => {
    setShowTypeSelectionModal(false);
    switch (type) {
      case 'documents':
        setShowAddDocumentModal(true);
        break;
      case 'prompt':
        setShowAddPromptModal(true);
        break;
      case 'qa':
        setShowAddQAModal(true);
        break;
    }
  };
  // Portal for CTA button
  useEffect(() => {
    const actionSlot = document.getElementById('page-action-slot');
    if (actionSlot) {
      const button = <AppButton icon={<Plus className="w-4 h-4" data-id="element-1068" />} onClick={() => setShowTypeSelectionModal(true)} data-id="element-1067">
          Aggiungi fonte
        </AppButton>;
      ReactDOM.render(button, actionSlot);
      return () => {
        ReactDOM.unmountComponentAtNode(actionSlot);
      };
    }
  }, []);
  // Map tab to source types
  const getTypeForTab = (tab: TabType): string[] => {
    switch (tab) {
      case 'documents':
        return ['Documenti'];
      case 'prompts':
        return ['Corso'];
      // Istruzioni Mirate use 'Corso' type
      case 'qa':
        return ['FAQ'];
      default:
        return [];
    }
  };
  const filteredSources = useMemo(() => {
    const typesForTab = getTypeForTab(activeTab);
    return sources.filter(source => {
      const matchesSearch = searchQuery === '' || source.name.toLowerCase().includes(searchQuery.toLowerCase()) || source.type.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTab = typesForTab.includes(source.type);
      const matchesStatus = statusFilter === 'Tutti' || source.status === statusFilter;
      return matchesSearch && matchesTab && matchesStatus;
    });
  }, [sources, searchQuery, activeTab, statusFilter]);
  const selectedSource = sources.find(s => s.id === selectedSourceId);
  const getStatusBadgeVariant = (status: KnowledgeSourceStatus): 'green' | 'amber' | 'gray' => {
    switch (status) {
      case 'Attivo':
        return 'green';
      case 'In aggiornamento':
        return 'amber';
      case 'Disattivato':
        return 'gray';
    }
  };
  const tabs = [{
    id: 'documents' as TabType,
    label: 'Documenti'
  }, {
    id: 'prompts' as TabType,
    label: 'Istruzioni Mirate'
  }, {
    id: 'qa' as TabType,
    label: 'Domande & Risposte'
  }];
  if (isLoading) {
    return <div className="flex items-center justify-center py-20" data-id="element-1069">
        <p className="text-gray-500 dark:text-gray-400" data-id="element-1070">Caricamento...</p>
      </div>;
  }
  return <div className="pb-12 space-y-6" data-id="element-1071">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} isVisible={!!toast} onClose={() => setToast(null)} data-id="element-1072" />}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-1073">
        <div data-id="element-1074">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-1075">
            Knowledge
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1076">
            Gestisci documenti, istruzioni e FAQ per il service
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-dark-subtle" data-id="element-1077">
        <nav className="flex gap-8" data-id="element-1078">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`pb-4 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-1079">
              {tab.label}
            </button>)}
        </nav>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4" data-id="element-1080">
        <div className="flex-1 relative" data-id="element-1081">
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-3" data-id="element-1082" />
          <input type="text" placeholder="Cerca per nome..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-180 shadow-sm" data-id="element-1083" />
        </div>

        <div className="relative min-w-[180px]" data-id="element-1084">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="appearance-none w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-700 dark:text-gray-300 py-2.5 pl-3.5 pr-10 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 cursor-pointer transition-all duration-180 shadow-sm" data-id="element-1085">
            <option value="Tutti" data-id="element-1086">Tutti gli stati</option>
            <option value="Attivo" data-id="element-1087">Attivo</option>
            <option value="In aggiornamento" data-id="element-1088">In aggiornamento</option>
            <option value="Disattivato" data-id="element-1089">Disattivato</option>
          </select>
          <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" data-id="element-1090" />
        </div>
      </div>

      {/* Content */}
      <AppCard padding="none" className="overflow-hidden" data-id="element-1091">
        <div className="overflow-x-auto" data-id="element-1092">
          <table className="w-full" data-id="element-1093">
            <thead className="bg-gray-50 dark:bg-dark-subtle border-b border-gray-100 dark:border-dark-medium" data-id="element-1094">
              <tr data-id="element-1095">
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1096">
                  Nome
                </th>
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1097">
                  Origine
                </th>
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1098">
                  Stato
                </th>
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1099">
                  Elementi
                </th>
                <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1100">
                  Aggiornato
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-dark-medium" data-id="element-1101">
              {filteredSources.length === 0 ? <tr data-id="element-1102">
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400 text-sm" data-id="element-1103">
                    Nessuna fonte trovata
                  </td>
                </tr> : filteredSources.map(source => <tr key={source.id} className="hover:bg-gray-50 dark:hover:bg-dark-hover cursor-pointer transition-colors duration-150" onClick={() => setSelectedSourceId(source.id)} data-id="element-1104">
                    <td className="px-6 py-4" data-id="element-1105">
                      <div className="flex items-center" data-id="element-1106">
                        <div className="p-1.5 bg-gray-100 dark:bg-dark-medium rounded-lg mr-3 text-gray-500 dark:text-gray-400" data-id="element-1107">
                          <FileText className="w-4 h-4" data-id="element-1108" />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-1109">
                          {source.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-1110">
                      <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1111">
                        {source.source}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-1112">
                      <AppBadge variant={getStatusBadgeVariant(source.status)} data-id="element-1113">
                        {source.status}
                      </AppBadge>
                    </td>
                    <td className="px-6 py-4" data-id="element-1114">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-1115">
                        {source.documentsCount}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-1116">
                      <span className="text-sm text-gray-500 dark:text-gray-500" data-id="element-1117">
                        {source.lastUpdated}
                      </span>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </AppCard>

      {/* Modals */}
      <SourceTypeSelectionModal isOpen={showTypeSelectionModal} onClose={() => setShowTypeSelectionModal(false)} onSelectType={handleTypeSelect} data-id="element-1118" />

      <AddDocumentSourceModal isOpen={showAddDocumentModal} onClose={() => setShowAddDocumentModal(false)} onCreate={handleCreate} data-id="element-1119" />

      <AddPromptSourceModal isOpen={showAddPromptModal} onClose={() => setShowAddPromptModal(false)} onCreate={handleCreate} data-id="element-1120" />

      <AddQASourceModal isOpen={showAddQAModal} onClose={() => setShowAddQAModal(false)} onCreate={handleCreate} data-id="element-1121" />

      <ViewSourceDetailModal isOpen={!!selectedSource} source={selectedSource} onClose={() => setSelectedSourceId(null)} onManage={source => {
      setSelectedSourceId(null);
      setManagingSource(source);
    }} getStatusBadgeVariant={getStatusBadgeVariant} data-id="element-1122" />

      <ManageKnowledgeSourceModal isOpen={!!managingSource} source={managingSource} onClose={() => setManagingSource(null)} onSave={handleSaveManage} onAddFile={handleAddFile} onRemoveFile={handleRemoveFile} data-id="element-1123" />
    </div>;
}